/**
 * LeadOutcomeDialog - Dialog for marking leads as won or lost
 *
 * Provides a consistent UX for closing leads with optional notes
 * and reason selection for lost leads.
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2, Trophy, ThumbsDown } from 'lucide-react'
import { LOST_REASONS } from '@limo/shared'

interface LeadOutcomeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  leadName: string
  outcome: 'won' | 'lost'
  onConfirm: (data: { outcomeNotes?: string; lostReason?: string }) => Promise<void>
  isLoading?: boolean
}

export function LeadOutcomeDialog({
  open,
  onOpenChange,
  leadName,
  outcome,
  onConfirm,
  isLoading = false,
}: LeadOutcomeDialogProps) {
  const [lostReason, setLostReason] = useState('')
  const [outcomeNotes, setOutcomeNotes] = useState('')

  const handleConfirm = async () => {
    await onConfirm({
      outcomeNotes: outcomeNotes.trim() || undefined,
      lostReason: outcome === 'lost' ? lostReason : undefined,
    })
    // Reset form on success
    setLostReason('')
    setOutcomeNotes('')
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset form when closing
      setLostReason('')
      setOutcomeNotes('')
    }
    onOpenChange(newOpen)
  }

  const isValid = outcome === 'won' || (outcome === 'lost' && lostReason)

  if (outcome === 'won') {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-green-600" />
              Mark Lead as Won
            </DialogTitle>
            <DialogDescription>
              Congratulations! This lead is converting to a booking.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              You are marking <strong>{leadName}</strong> as won.
            </p>

            <div className="space-y-2">
              <Label htmlFor="outcome-notes">Notes (optional)</Label>
              <Textarea
                id="outcome-notes"
                placeholder="Add any notes about this win..."
                value={outcomeNotes}
                onChange={(e) => setOutcomeNotes(e.target.value)}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                These notes will be visible in the lead history.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Trophy className="mr-2 h-4 w-4" />
              Confirm Won
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  // Lost dialog
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ThumbsDown className="h-5 w-5 text-gray-500" />
            Mark Lead as Lost
          </DialogTitle>
          <DialogDescription>
            Please select a reason for losing this lead.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="lost-reason">Reason *</Label>
            <Select value={lostReason} onValueChange={setLostReason}>
              <SelectTrigger id="lost-reason">
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                {LOST_REASONS.map((reason) => (
                  <SelectItem key={reason} value={reason}>
                    {reason}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="outcome-notes">Additional Notes (optional)</Label>
            <Textarea
              id="outcome-notes"
              placeholder="Add more details about why this lead was lost..."
              value={outcomeNotes}
              onChange={(e) => setOutcomeNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isValid || isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Mark Lost
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Simpler hook-based approach for using the dialog
 */
export function useLeadOutcomeDialog() {
  const [state, setState] = useState<{
    open: boolean
    outcome: 'won' | 'lost'
    leadId: string
    leadName: string
    onConfirm: ((data: { outcomeNotes?: string; lostReason?: string }) => Promise<void>) | null
  }>({
    open: false,
    outcome: 'won',
    leadId: '',
    leadName: '',
    onConfirm: null,
  })

  const openWonDialog = (
    leadId: string,
    leadName: string,
    onConfirm: (data: { outcomeNotes?: string }) => Promise<void>
  ) => {
    setState({
      open: true,
      outcome: 'won',
      leadId,
      leadName,
      onConfirm,
    })
  }

  const openLostDialog = (
    leadId: string,
    leadName: string,
    onConfirm: (data: { outcomeNotes?: string; lostReason?: string }) => Promise<void>
  ) => {
    setState({
      open: true,
      outcome: 'lost',
      leadId,
      leadName,
      onConfirm,
    })
  }

  const close = () => {
    setState((prev) => ({ ...prev, open: false }))
  }

  return {
    state,
    openWonDialog,
    openLostDialog,
    close,
    setOpen: (open: boolean) => setState((prev) => ({ ...prev, open })),
  }
}
